import stationSt001 from '../assets/station-st-001-2025-01-15.png';
import stationSt002 from '../assets/station-st-002-2025-01-14.png';
import stationSt003 from '../assets/station-st-003-2025-01-13.png';
import { getModuleData } from './mockModuleData';
import { resolveLanguageCode } from './locale';

const REPORT_MODULES_BY_TYPE = {
  relazione_agronomica_primo_anno: [
    {
      id: 'm0',
      number: 1,
      nameKey: 'reports.workspace.firstYearModules.m0.name',
      descriptionKey: 'reports.workspace.firstYearModules.m0.description',
      fallbackName: 'System setup & sensors',
      fallbackDescription: 'Station hardware, sensors, and monitoring scope.',
    },
    {
      id: 'm1',
      number: 2,
      nameKey: 'reports.workspace.firstYearModules.m1.name',
      descriptionKey: 'reports.workspace.firstYearModules.m1.description',
      fallbackName: 'Station distribution & coverage',
      fallbackDescription: 'Station placement, coverage map, and density checks.',
    },
  ],
  relazione_agronomica_annuale: [
    {
      id: 'm1',
      number: 1,
      nameKey: 'reports.workspace.modules.m1.name',
      descriptionKey: 'reports.workspace.modules.m1.description',
      fallbackName: 'Production trends & benchmark',
      fallbackDescription: 'Production performance versus RICA benchmarks.',
    },
    {
      id: 'm2',
      number: 2,
      nameKey: 'reports.workspace.modules.m2.name',
      descriptionKey: 'reports.workspace.modules.m2.description',
      fallbackName: 'Soil fertility recovery',
      fallbackDescription: 'Soil fertility monitoring and recovery signals.',
    },
    {
      id: 'm3',
      number: 3,
      nameKey: 'reports.workspace.modules.m3.name',
      descriptionKey: 'reports.workspace.modules.m3.description',
      fallbackName: 'Water savings',
      fallbackDescription: 'Water savings and irrigation evidence.',
    },
    {
      id: 'm4',
      number: 4,
      nameKey: 'reports.workspace.modules.m4.name',
      descriptionKey: 'reports.workspace.modules.m4.description',
      fallbackName: 'Microclimate',
      fallbackDescription: 'Temperature, humidity, and irradiation differences.',
    },
  ],
};

const resolveText = (locale, italian, english, spanish = '') => {
  const languageCode = resolveLanguageCode(locale);
  if (languageCode === 'en') {
    return english || italian;
  }
  if (languageCode === 'es') {
    return spanish || english || italian;
  }
  return italian;
};

const createBlock = (overrides) => ({
  id: '',
  type: 'text',
  title: '',
  status: 'draft',
  moduleId: '',
  reportType: '',
  sectionType: '',
  sectionId: '',
  sectionTitle: '',
  sectionOrder: 0,
  sensorId: '',
  sensorName: '',
  sensorUnit: '',
  sensorPeriod: '',
  blockRole: '',
  blockOrder: 0,
  metadata: [],
  bodyText: '',
  bodyLabel: '',
  bodyLabelKey: '',
  bodyPlaceholder: '',
  bodyPlaceholderKey: '',
  caption: '',
  captionLabelKey: '',
  captionPlaceholder: '',
  captionPlaceholderKey: '',
  chart: null,
  tableData: null,
  imageList: [],
  imageLayout: 'grid',
  imageGroup: '',
  commentDraft: '',
  commentHistory: [],
  lastEditedBy: null,
  lastEditedAt: null,
  approvedBy: null,
  approvedAt: null,
  ...overrides,
});

const cloneBlocks = (blocks) => JSON.parse(JSON.stringify(blocks || []));

const PRODUCTION_BENCHMARK_THRESHOLD = 0.3;

export const COVERAGE_MOCK = {
  coverageThreshold: 2,
  zones: [
    {
      id: 'zone-a',
      name: 'Zona A',
      nameEn: 'Zone A',
      areaHa: 1.2,
      geometry: [
        [42.3252, 12.421],
        [42.3252, 12.4234],
        [42.3238, 12.4234],
        [42.3238, 12.421],
      ],
    },
    {
      id: 'zone-b',
      name: 'Zona B',
      nameEn: 'Zone B',
      areaHa: 2.4,
      geometry: [
        [42.3238, 12.421],
        [42.3238, 12.4234],
        [42.3222, 12.4234],
        [42.3222, 12.421],
      ],
    },
    {
      id: 'zone-c',
      name: 'Zona C',
      nameEn: 'Zone C',
      areaHa: 0.9,
      geometry: [
        [42.3252, 12.4234],
        [42.3252, 12.4256],
        [42.3238, 12.4256],
        [42.3238, 12.4234],
      ],
    },
  ],
  stations: [
    {
      id: 'st-01',
      name: 'ST-01',
      type: 'retro_modulo',
      coordinates: [42.3248, 12.4221],
      zoneId: 'zone-a',
    },
    {
      id: 'st-02',
      name: 'ST-02',
      type: 'campo_aperto',
      coordinates: [42.3243, 12.4227],
      zoneId: 'zone-a',
    },
    {
      id: 'st-03',
      name: 'ST-03',
      type: 'retro_modulo',
      coordinates: [42.3245, 12.4229],
    },
    {
      id: 'st-04',
      name: 'ST-04',
      type: 'campo_aperto',
      coordinates: [42.323, 12.422],
    },
    {
      id: 'st-05',
      name: 'ST-05',
      type: 'campo_aperto',
      coordinates: [42.3228, 12.4226],
      zoneId: 'zone-b',
    },
    {
      id: 'st-06',
      name: 'ST-06',
      type: 'retro_modulo',
      coordinates: [42.3246, 12.4244],
      zoneId: 'zone-c',
    },
    {
      id: 'st-07',
      name: 'ST-07',
      type: 'campo_aperto',
      coordinates: [42.324, 12.4249],
    },
  ],
};

const buildFirstYearBlocks = (moduleId, reportType, locale) => {
  if (moduleId === 'm0') {
    return [
      createBlock({
        id: 'm0-intro-summary',
        type: 'text',
        title: resolveText(locale, 'Sintesi introduttiva', 'Introductory summary', 'Resumen introductorio'),
        moduleId,
        reportType,
        bodyText: resolveText(
          locale,
          "Questo modulo descrive l'impianto agrivoltaico, le stazioni di monitoraggio e l'inventario sensori introdotti nel primo anno. Le informazioni sono precompilate per consentire una rapida validazione e personalizzazione da parte dell'agronomo.",
          'This module describes the agrivoltaic system, monitoring stations, and sensor inventory introduced in the first year. The information is prefilled to enable quick validation and customization by the agronomist.'
        ),
      }),
      createBlock({
        id: 'm0-hardware-overview',
        type: 'text',
        title: resolveText(locale, 'Architettura hardware e componenti', 'Hardware architecture and components', 'Arquitectura y componentes de hardware.'),
        moduleId,
        reportType,
        bodyText: resolveText(locale, 'Le stazioni sono progettate per operare in autonomia e raccogliere dati ambientali e agronomici in modo continuo. Ogni stazione include datalogger, sensori microclimatici e del suolo, alimentazione solare con batteria tampone e connettivita cellulare.', 'Stations are designed to operate autonomously and collect environmental and agronomic data continuously. Each station includes a data logger, microclimate and soil sensors, solar power with backup battery, and cellular connectivity.', 'Las estaciones están diseñadas para funcionar de forma autónoma y recopilar datos ambientales y agronómicos de forma continua. Cada estación incluye un registrador de datos, sensores de microclima y suelo, energía solar con batería de respaldo y conectividad celular.'),
      }),
      createBlock({
        id: 'm0-hardware-details',
        type: 'text',
        title: resolveText(locale, 'Trasmissione dati, alimentazione e manutenzione', 'Data transmission, power, and maintenance', 'Transmisión de datos, energía y mantenimiento.'),
        moduleId,
        reportType,
        bodyText: resolveText(locale, 'Trasmissione dati: invio orario (60 minuti) via LTE/NB-IoT con sincronizzazione giornaliera.\nAlimentazione: pannello solare + batteria, autonomia superiore a 7 giorni.\nManutenzione: controllo trimestrale, pulizia sensori e verifica cablaggi.', 'Data transmission: hourly (60 min) upload via LTE/NB-IoT with daily sync.\nPower: solar panel + battery, autonomy over 7 days.\nMaintenance: quarterly inspection, sensor cleaning, and wiring checks.', 'Transmisión de datos: carga horaria (60 min) vía LTE/NB-IoT con sincronización diaria.\nPotencia: panel solar + batería, autonomía superior a 7 días.\nMantenimiento: inspección trimestral, limpieza de sensores y revisión de cableado.'),
      }),
      createBlock({
        id: 'm0-hardware-photos',
        type: 'image',
        title: resolveText(locale, 'Componenti hardware', 'Hardware components', 'Componentes de hardware'),
        moduleId,
        reportType,
        imageLayout: 'grid',
        imageList: [
          {
            url: stationSt003,
            alt: resolveText(locale, 'Dettaglio sensori e datalogger', 'Sensors and datalogger detail', 'Detalle de sensores y registrador de datos.'),
            caption: resolveText(locale, 'Dettaglio della dotazione sensori e del datalogger installati sulla stazione. L\'immagine documenta cablaggio e posizionamento dei componenti per la raccolta continua dei dati. Serve come evidenza visiva della configurazione hardware del primo anno.', 'Detail of the sensor set and datalogger installed on the station. The image documents wiring and component placement for continuous data collection. It serves as visual evidence of the first year hardware configuration.', 'Detalle del conjunto de sensores y datalogger instalados en la estación. La imagen documenta el cableado y la ubicación de los componentes para la recopilación continua de datos. Sirve como evidencia visual de la configuración de hardware del primer año.'),
          },
          {
            url: stationSt001,
            alt: resolveText(locale, 'Stazione con pannello solare', 'Station with solar panel', 'Estación con panel solar'),
            caption: resolveText(locale, 'La stazione con pannello solare mostra il sistema di alimentazione autonoma adottato in campo. Il posizionamento evidenzia l\'orientamento del pannello e la protezione dei componenti. Questa foto supporta la verifica delle condizioni di installazione e manutenzione.', 'The station with solar panel shows the autonomous power system used in the field. The positioning highlights panel orientation and component protection. This photo supports verification of installation and maintenance conditions.', 'La estación con panel solar muestra el sistema de energía autónomo utilizado en el campo. El posicionamiento resalta la orientación del panel y la protección de los componentes. Esta foto respalda la verificación de las condiciones de instalación y mantenimiento.'),
          },
        ],
      }),
      createBlock({
        id: 'm0-sensor-inventory',
        type: 'table',
        title: resolveText(locale, 'Inventario sensori e pillar associati', 'Sensor inventory and associated pillars', 'Inventario de sensores y pilares asociados'),
        moduleId,
        reportType,
        tableData: {
          editable: true,
          editableHeaders: true,
          columns: [
            { key: 'sensor', label: '', labelKey: 'reports.workspace.blocks.tableHeaders.sensor', editable: true },
            { key: 'unit', label: '', labelKey: 'reports.workspace.blocks.tableHeaders.unit', editable: true },
            { key: 'description', label: '', labelKey: 'reports.workspace.blocks.tableHeaders.description', editable: true, multiline: true },
            {
              key: 'pillars',
              label: '',
              labelKey: 'reports.workspace.blocks.tableHeaders.pillars',
              type: 'tags',
              editable: true,
              options: [
                { value: 'water', labelKey: 'reports.workspace.pillars.water' },
                { value: 'soil', labelKey: 'reports.workspace.pillars.soil' },
                { value: 'microclimate', labelKey: 'reports.workspace.pillars.microclimate' },
              ],
            },
          ],
          rows: [
            {
              sensor: resolveText(locale, 'Umidita del suolo', 'Soil moisture', 'Humedad del suelo'),
              unit: '% VWC',
              description: resolveText(locale, 'Valuta la riserva idrica del profilo e supporta la gestione irrigua.', 'Assesses profile water reserve and supports irrigation management.', 'Evalúa el perfil de reserva de agua y apoya la gestión del riego.'),
              pillars: ['water', 'soil'],
            },
            {
              sensor: resolveText(locale, 'Temperatura del suolo', 'Soil temperature', 'Temperatura del suelo'),
              unit: 'C',
              description: resolveText(locale, 'Misura la dinamica termica del suolo e il comfort radicale.', 'Measures soil thermal dynamics and root comfort.', 'Mide la dinámica térmica del suelo y el confort de las raíces.'),
              pillars: ['soil'],
            },
            {
              sensor: resolveText(locale, 'Temperatura aria', 'Air temperature', 'Temperatura del aire'),
              unit: 'C',
              description: resolveText(locale, 'Registra la variabilita termica tra retro modulo e campo aperto.', 'Records thermal variability between rear module and open field.', 'Registra la variabilidad térmica entre el módulo trasero y el campo abierto.'),
              pillars: ['microclimate'],
            },
            {
              sensor: resolveText(locale, 'Umidita relativa', 'Relative humidity', 'Humedad relativa'),
              unit: '%',
              description: resolveText(locale, 'Supporta la lettura del microclima e del rischio patogeni.', 'Supports microclimate assessment and disease risk.', 'Apoya la evaluación del microclima y el riesgo de enfermedades.'),
              pillars: ['microclimate'],
            },
            {
              sensor: resolveText(locale, 'Irradiazione globale', 'Global irradiation', 'Irradiación global'),
              unit: 'W/m2',
              description: resolveText(
                locale,
                "Quantifica l'energia luminosa disponibile per le colture.",
                'Quantifies light energy available to crops.'
              ),
              pillars: ['microclimate'],
            },
            {
              sensor: resolveText(locale, 'Pioggia utile', 'Effective rainfall', 'Precipitación efectiva'),
              unit: 'mm',
              description: resolveText(locale, 'Misura le precipitazioni utili al bilancio idrico.', 'Measures rainfall useful for the water balance.', 'Mide las precipitaciones útiles para el balance hídrico.'),
              pillars: ['water'],
            },
            {
              sensor: resolveText(locale, 'Velocita del vento', 'Wind speed', 'Velocidad del viento'),
              unit: 'm/s',
              description: resolveText(locale, 'Supporta stime di evapotraspirazione e stress della coltura.', 'Supports evapotranspiration and crop stress estimates.', 'Apoya las estimaciones de evapotranspiración y estrés de los cultivos.'),
              pillars: ['microclimate', 'water'],
            },
          ],
        },
        bodyText: resolveText(
          locale,
          "La tabella riassume i sensori installati e il pillar a cui contribuiscono. Le associazioni possono essere modificate dall'agronomo se necessario.",
          'The table summarizes installed sensors and the pillar they contribute to. Associations can be adjusted by the agronomist if needed.'
        ),
        bodyLabelKey: 'reports.workspace.blocks.labels.tableNote',
      }),
      createBlock({
        id: 'm0-installation-photos',
        type: 'image',
        title: resolveText(locale, 'Foto installazione', 'Installation photos', 'Fotos de instalación'),
        moduleId,
        reportType,
        imageLayout: 'grid',
        imageList: [
          {
            url: stationSt001,
            alt: resolveText(locale, 'Installazione stazione - retro modulo', 'Station installation - rear module', 'Instalación de la estación - módulo trasero'),
            caption: resolveText(locale, 'Scatto durante l\'installazione in area retro modulo, con dettaglio del posizionamento della stazione. La foto evidenzia l\'integrazione con la struttura dei pannelli e l\'accessibilita per le operazioni di servizio. Fornisce evidenza delle condizioni di posa nel primo anno.', 'Shot during installation in the rear module area, with detail of station placement. The photo highlights integration with the panel structure and service access. It provides evidence of installation conditions in the first year.', 'Tomada durante la instalación en la zona trasera del módulo, con detalle de colocación de la estación. En la foto se destaca la integración con la estructura del panel y el acceso al servicio. Proporciona evidencia de las condiciones de instalación en el primer año.'),
          },
          {
            url: stationSt002,
            alt: resolveText(locale, 'Installazione stazione - campo aperto', 'Station installation - open field', 'Instalación de la estación - campo abierto'),
            caption: resolveText(locale, 'Installazione in campo aperto con visuale della stazione e dell\'area circostante. L\'immagine mostra l\'ancoraggio e l\'altezza di montaggio adottata per il monitoraggio. Serve a documentare la configurazione standard fuori dall\'ombra dei moduli.', 'Installation in open field with view of the station and surrounding area. The image shows anchoring and mounting height used for monitoring. It documents the standard configuration outside panel shade.', 'Instalación en campo abierto con vista a la estación y alrededores. La imagen muestra la altura de anclaje y montaje utilizada para el monitoreo. Documenta la configuración estándar de la persiana del panel exterior.'),
          },
        ],
      }),
    ];
  }

  if (moduleId === 'm1') {
    return [
      createBlock({
        id: 'm1-station-map',
        type: 'chart',
        title: resolveText(locale, 'Mappa distribuzione stazioni', 'Station distribution map', 'Mapa de distribución de estaciones.'),
        moduleId,
        reportType,
        chart: {
          variant: 'map',
          legendVariant: 'stations',
          legendTitleKey: 'reports.workspace.blocks.coverage.legendTitle',
          alwaysShowMarkers: true,
          showMarkerTooltips: true,
          ariaLabelKey: 'reports.workspace.blocks.coverage.mapAlt',
        },
        bodyText: resolveText(locale, 'La mappa mostra la distribuzione delle stazioni nelle diverse zone del sito, distinguendo tra retro modulo e campo aperto. La disposizione evidenzia la copertura complessiva e le aree con maggiore densita di monitoraggio. Questa vista permette di verificare rapidamente l\'equilibrio tra le due tipologie di installazione.', 'The map shows station distribution across site zones, distinguishing rear module and open field placements. The layout highlights overall coverage and areas with higher monitoring density. This view helps validate the balance between the two installation types.', 'El mapa muestra la distribución de las estaciones en las zonas del sitio, distinguiendo las ubicaciones del módulo trasero y del campo abierto. El diseño destaca la cobertura general y las áreas con mayor densidad de monitoreo. Esta vista ayuda a validar el equilibrio entre los dos tipos de instalación.'),
        bodyLabelKey: 'reports.workspace.blocks.labels.caption',
        bodyPlaceholderKey: 'reports.workspace.blocks.placeholders.mapCaption',
      }),
      createBlock({
        id: 'm1-coverage-table',
        type: 'table',
        title: resolveText(locale, 'Copertura per zona', 'Coverage by zone', 'Cobertura por zona'),
        moduleId,
        reportType,
        tableData: {
          source: 'stationCoverage',
          coverageThreshold: 2,
          showTotalRow: true,
          coverage: COVERAGE_MOCK,
          columns: [
            {
              key: 'zone',
              label: '',
              labelKey: 'reports.workspace.blocks.tableHeaders.zone',
              isRowHeader: true,
            },
            {
              key: 'area',
              label: '',
              labelKey: 'reports.workspace.blocks.tableHeaders.surfaceHa',
            },
            {
              key: 'retroStations',
              label: '',
              labelKey: 'reports.workspace.blocks.tableHeaders.retroStations',
            },
            {
              key: 'campoStations',
              label: '',
              labelKey: 'reports.workspace.blocks.tableHeaders.campoStations',
            },
            {
              key: 'totalStations',
              label: '',
              labelKey: 'reports.workspace.blocks.tableHeaders.totalStations',
            },
            {
              key: 'stationsPerHa',
              label: '',
              labelKey: 'reports.workspace.blocks.tableHeaders.stationsPerHa',
              type: 'badge',
            },
          ],
        },
        bodyText: resolveText(locale, 'La tabella riassume la copertura per zona, includendo superficie, numero di stazioni e densita per ettaro. Le righe sotto soglia indicano dove la rete di monitoraggio puo essere rafforzata. Il riepilogo guida le priorita di installazione e manutenzione.', 'The table summarizes coverage by zone, including area, station counts, and density per hectare. Rows below the threshold indicate where the monitoring network should be strengthened. This summary guides installation and maintenance priorities.', 'La tabla resume la cobertura por zona, incluyendo área, recuento de estaciones y densidad por hectárea. Las filas por debajo del umbral indican dónde se debe fortalecer la red de monitoreo. Este resumen orienta las prioridades de instalación y mantenimiento.'),
        bodyLabelKey: 'reports.workspace.blocks.labels.caption',
        bodyPlaceholderKey: 'reports.workspace.blocks.placeholders.tableCaption',
      }),
      createBlock({
        id: 'm1-coverage-strategy',
        type: 'text',
        title: resolveText(locale, 'Strategia di copertura', 'Coverage strategy', 'Estrategia de cobertura'),
        moduleId,
        reportType,
        bodyText: resolveText(locale, 'La strategia prevede una densita minima di 2 stazioni per ettaro, con un bilanciamento tra retro modulo e campo aperto.\nLe stazioni sono posizionate in prossimita delle aree a maggiore variabilita per garantire letture rappresentative.', 'The strategy targets a minimum density of 2 stations per hectare, balancing rear module and open field coverage.\nStations are placed near areas with higher variability to ensure representative readings.', 'La estrategia apunta a una densidad mínima de 2 estaciones por hectárea, equilibrando el módulo trasero y la cobertura de campo abierto.\nLas estaciones se colocan cerca de áreas con mayor variabilidad para garantizar lecturas representativas.'),
        bodyPlaceholderKey: 'reports.workspace.blocks.placeholders.coverageNarrative',
      }),
    ];
  }

  return [];
};

const toNumber = (value) => {
  if (value == null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value, decimals = 2) => {
  const numeric = toNumber(value);
  if (numeric == null) {
    return '';
  }
  return numeric.toFixed(decimals);
};

const formatPercent = (ratio, decimals = 0) => {
  if (ratio == null) {
    return '';
  }
  return `${(ratio * 100).toFixed(decimals)}%`;
};

const getProductionRatio = (production, benchmark) => {
  const prod = toNumber(production);
  const bench = toNumber(benchmark);
  if (prod == null || bench == null || bench === 0) {
    return null;
  }
  return prod / bench;
};

const buildProductionTrendBlocks = (moduleId, reportType, locale) => {
  const moduleData = getModuleData(moduleId, locale);
  const productionData = Array.isArray(moduleData?.evidence?.productionData)
    ? moduleData.evidence.productionData
    : [];
  const chartData = productionData
    .map(item => ({
      label: item.month || item.period || '',
      value: toNumber(item.production),
    }))
    .filter(point => point.label || point.value != null);
  const statusBelowLabel = resolveText(locale, 'Sotto 30%', 'Below 30%', 'Por debajo de 30%');
  const statusOkLabel = resolveText(locale, 'OK', 'OK', 'DE ACUERDO');
  const rows = productionData.map(item => {
    const ratio = getProductionRatio(item.production, item.ricaBenchmark);
    const percent = formatPercent(ratio, 0);
    const isBelow = ratio != null && ratio < PRODUCTION_BENCHMARK_THRESHOLD;
    return {
      period: item.month || item.period || '',
      production: formatNumber(item.production),
      benchmark: formatNumber(item.ricaBenchmark),
      percentageOfBenchmark: percent,
      status: ratio == null ? '-' : (isBelow ? statusBelowLabel : statusOkLabel),
    };
  });
  const hasBelowThreshold = productionData.some(item => {
    const ratio = getProductionRatio(item.production, item.ricaBenchmark);
    return ratio != null && ratio < PRODUCTION_BENCHMARK_THRESHOLD;
  });

  const blocks = [
    createBlock({
      id: 'm1-station-map',
      type: 'chart',
      title: resolveText(locale, 'Mappa distribuzione stazioni', 'Station distribution map', 'Mapa de distribución de estaciones.'),
      moduleId,
      reportType,
      blockRole: 'map',
      chart: {
        variant: 'map',
        legendVariant: 'stations',
        legendTitleKey: 'reports.workspace.blocks.coverage.legendTitle',
        alwaysShowMarkers: true,
        showMarkerTooltips: true,
        ariaLabelKey: 'reports.workspace.blocks.coverage.mapAlt',
      },
      fallbackCoverage: COVERAGE_MOCK,
      bodyText: resolveText(locale, 'La mappa mostra la distribuzione delle stazioni nelle diverse zone del sito, distinguendo tra retro modulo e campo aperto. La disposizione evidenzia la copertura complessiva e le aree con maggiore densita di monitoraggio. Questa vista permette di verificare rapidamente l\'equilibrio tra le due tipologie di installazione.', 'The map shows station distribution across site zones, distinguishing rear module and open field placements. The layout highlights overall coverage and areas with higher monitoring density. This view helps validate the balance between the two installation types.', 'El mapa muestra la distribución de las estaciones en las zonas del sitio, distinguiendo las ubicaciones del módulo trasero y del campo abierto. El diseño destaca la cobertura general y las áreas con mayor densidad de monitoreo. Esta vista ayuda a validar el equilibrio entre los dos tipos de instalación.'),
      bodyLabelKey: 'reports.workspace.blocks.labels.caption',
      bodyPlaceholderKey: 'reports.workspace.blocks.placeholders.mapCaption',
    }),
    createBlock({
      id: 'm1-production-trend',
      type: 'chart',
      title: resolveText(locale, 'Andamento produzione', 'Production trend', 'Tendencia de producción'),
      moduleId,
      reportType,
      blockRole: 'chart',
      chart: {
        variant: 'line',
        data: chartData,
        xKey: 'label',
        yKey: 'value',
        unit: 't/ha',
      },
      bodyText: resolveText(locale, 'Il grafico sintetizza l\'andamento della produzione nei mesi monitorati, con flessioni nei periodi estivi e recuperi parziali nelle fasi intermedie. I valori restano sotto il benchmark RICA e indicano una distanza strutturale rispetto alle attese. La lettura supporta il confronto con pratiche irrigue e interventi agronomici del periodo.', 'The chart summarizes production trends across monitored months, with dips in summer periods and partial recoveries in intermediate phases. Values remain below the RICA benchmark and indicate a structural gap versus expectations. The reading supports comparison with irrigation practices and agronomic interventions of the period.', 'El gráfico resume las tendencias de producción a lo largo de los meses monitoreados, con caídas en los períodos de verano y recuperaciones parciales en las fases intermedias. Los valores se mantienen por debajo del punto de referencia de RICA e indican una brecha estructural frente a las expectativas. La lectura sustenta la comparación con prácticas de riego e intervenciones agronómicas del período.'),
      bodyLabelKey: 'reports.workspace.blocks.labels.caption',
      bodyPlaceholderKey: 'reports.workspace.blocks.placeholders.chartCaption',
    }),
    createBlock({
      id: 'm1-production-table',
      type: 'table',
      title: resolveText(locale, 'Produzione vs benchmark', 'Production vs benchmark', 'Producción versus punto de referencia'),
      moduleId,
      reportType,
      blockRole: 'table',
      tableData: {
        editable: true,
        columns: [
          {
            key: 'period',
            label: '',
            labelKey: 'reports.workspace.blocks.tableHeaders.period',
            isRowHeader: true,
          },
          {
            key: 'production',
            label: '',
            labelKey: 'reports.workspace.blocks.tableHeaders.production',
          },
          {
            key: 'benchmark',
            label: '',
            labelKey: 'reports.workspace.blocks.tableHeaders.benchmark',
          },
          {
            key: 'percentageOfBenchmark',
            label: '',
            labelKey: 'reports.workspace.blocks.tableHeaders.percentageOfBenchmark',
          },
          {
            key: 'status',
            label: '',
            labelKey: 'reports.workspace.blocks.tableHeaders.status',
          },
        ],
        rows,
      },
      bodyText: resolveText(locale, 'La tabella confronta produzione e benchmark per ciascun periodo, includendo la percentuale di scostamento rispetto al riferimento RICA. La colonna di stato mette in evidenza i mesi sotto la soglia del 30%, facilitando l\'identificazione delle fasi critiche. Questo riepilogo supporta la validazione dei dati e la motivazione delle deviazioni nel report.', 'The table compares production and benchmark for each period, including the percentage deviation from the RICA reference. The status column highlights months below the 30% threshold, making critical phases easier to identify. This summary supports data validation and justification of deviations in the report.', 'El cuadro compara la producción y el punto de referencia para cada período, incluida la desviación porcentual de la referencia RICA. La columna de estado resalta los meses por debajo del umbral 30%, lo que facilita la identificación de las fases críticas. Este resumen respalda la validación de datos y la justificación de desviaciones en el informe.'),
      bodyLabelKey: 'reports.workspace.blocks.labels.caption',
      bodyPlaceholderKey: 'reports.workspace.blocks.placeholders.tableCaption',
    }),
    createBlock({
      id: 'm1-production-interpretation',
      type: 'text',
      title: '',
      moduleId,
      reportType,
      blockRole: 'interpretation',
      bodyText: moduleData?.aiInsight?.summary || '',
      bodyLabelKey: 'reports.workspace.blocks.labels.interpretation',
      bodyPlaceholderKey: 'reports.workspace.blocks.placeholders.productionNarrative',
    }),
  ];

  if (hasBelowThreshold) {
    blocks.push(
      createBlock({
        id: 'm1-production-callout',
        type: 'text',
        title: '',
        moduleId,
        reportType,
        blockRole: 'callout',
        bodyText: moduleData?.review?.summary || '',
        bodyLabelKey: 'reports.workspace.blocks.labels.narrative',
        bodyPlaceholderKey: 'reports.workspace.blocks.placeholders.productionCallout',
      })
    );
  }

  return blocks;
};

const getChartPeriodLabel = (chart) => {
  if (!chart) {
    return '';
  }
  if (chart.period) {
    return chart.period;
  }
  const data = Array.isArray(chart.data) ? chart.data : [];
  if (data.length === 0) {
    return '';
  }
  const firstLabel = data[0]?.label;
  const lastLabel = data[data.length - 1]?.label;
  if (firstLabel && lastLabel) {
    return firstLabel === lastLabel ? String(firstLabel) : `${firstLabel} - ${lastLabel}`;
  }
  return '';
};

const getSensorPeriodLabel = (charts) => {
  if (!Array.isArray(charts) || charts.length === 0) {
    return '';
  }
  const uniquePeriods = Array.from(
    new Set(charts.map(getChartPeriodLabel).filter(Boolean))
  );
  if (uniquePeriods.length === 0) {
    return '';
  }
  return uniquePeriods.join(' / ');
};

const getSensorUnit = (sensor) => {
  if (!sensor) {
    return '';
  }
  if (sensor.unit) {
    return sensor.unit;
  }
  const chartUnit = sensor.charts?.find(chart => chart?.unit)?.unit;
  return chartUnit || '';
};

const buildAnnualBlocks = (moduleId, reportType, locale) => {
  const moduleData = getModuleData(moduleId, locale);
  const sensors = moduleData?.evidence?.sensors || [];
  const blocks = [];

  sensors.forEach((sensor, sensorIndex) => {
    const charts = (sensor.charts || []).slice(0, 2);
    const sensorUnit = getSensorUnit(sensor);
    const sensorPeriod = getSensorPeriodLabel(charts);
    const sectionId = `${moduleId}-${sensor.id}`;
    const sectionTitle = sensor.name || '';
    const baseMetadata = [
      { labelKey: 'reports.workspace.blocks.metadata.sensor', value: sensor.name },
    ];

    charts.forEach((chart, chartIndex) => {
      blocks.push(
        createBlock({
          id: `${moduleId}-${sensor.id}-${chart.id}`,
          type: 'chart',
          title: chart.title || '',
          moduleId,
          reportType,
          sectionType: 'sensor',
          sectionId,
          sectionTitle,
          sectionOrder: sensorIndex,
          sensorId: sensor.id,
          sensorName: sensor.name,
          sensorUnit,
          sensorPeriod,
          blockRole: 'chart',
          blockOrder: chartIndex,
          metadata: [
            ...baseMetadata,
            { labelKey: 'reports.workspace.blocks.metadata.unit', value: chart.unit },
            { labelKey: 'reports.workspace.blocks.metadata.period', value: getChartPeriodLabel(chart) },
          ],
          chart: {
            variant: 'line',
            data: chart.data || [],
            xKey: 'label',
            yKey: 'value',
            unit: chart.unit,
          },
          caption: chart.note || '',
          captionPlaceholderKey: 'reports.workspace.blocks.placeholders.chartCaption',
          bodyText: chart.note || '',
          bodyLabelKey: 'reports.workspace.blocks.labels.caption',
        })
      );
    });

    if (sensor.tableData) {
      blocks.push(
        createBlock({
          id: `${moduleId}-${sensor.id}-supporting-table`,
          type: 'table',
          title: sensor.tableTitle || resolveText(locale, 'Tabella di supporto', 'Supporting table', 'mesa de apoyo'),
          moduleId,
          reportType,
          sectionType: 'sensor',
          sectionId,
          sectionTitle,
          sectionOrder: sensorIndex,
          sensorId: sensor.id,
          sensorName: sensor.name,
          sensorUnit,
          sensorPeriod,
          blockRole: 'table',
          blockOrder: 2,
          metadata: baseMetadata,
          tableData: sensor.tableData,
          bodyText: sensor.tableCaption || '',
          bodyLabelKey: 'reports.workspace.blocks.labels.caption',
        })
      );
    }

    blocks.push(
      createBlock({
        id: `${moduleId}-${sensor.id}-retro-modulo`,
        type: 'image',
        title: resolveText(locale, 'Retro modulo', 'Rear module', 'Módulo trasero'),
        moduleId,
        reportType,
        sectionType: 'sensor',
        sectionId,
        sectionTitle,
        sectionOrder: sensorIndex,
        sensorId: sensor.id,
        sensorName: sensor.name,
        sensorUnit,
        sensorPeriod,
        blockRole: 'image',
          blockOrder: 3,
        imageGroup: 'retro_modulo',
        metadata: baseMetadata,
        imageLayout: 'grid',
        imageList: (sensor.images?.retroModulo || []).map((image) => ({
          url: image.url,
          alt: image.alt,
          caption: image.caption || image.alt || '',
        })),
      })
    );

    blocks.push(
      createBlock({
        id: `${moduleId}-${sensor.id}-campo-aperto`,
        type: 'image',
        title: resolveText(locale, 'Campo aperto', 'Open field', 'campo abierto'),
        moduleId,
        reportType,
        sectionType: 'sensor',
        sectionId,
        sectionTitle,
        sectionOrder: sensorIndex,
        sensorId: sensor.id,
        sensorName: sensor.name,
        sensorUnit,
        sensorPeriod,
        blockRole: 'image',
          blockOrder: 4,
        imageGroup: 'campo_aperto',
        metadata: baseMetadata,
        imageLayout: 'grid',
        imageList: (sensor.images?.campoAperto || []).map((image) => ({
          url: image.url,
          alt: image.alt,
          caption: image.caption || image.alt || '',
        })),
      })
    );

    blocks.push(
      createBlock({
        id: `${moduleId}-${sensor.id}-interpretation`,
        type: 'text',
        title: '',
        moduleId,
        reportType,
        sectionType: 'sensor',
        sectionId,
        sectionTitle,
        sectionOrder: sensorIndex,
        sensorId: sensor.id,
        sensorName: sensor.name,
        sensorUnit,
        sensorPeriod,
        blockRole: 'interpretation',
        blockOrder: 5,
        metadata: baseMetadata,
        bodyText: sensor.explanation || '',
        bodyLabelKey: 'reports.workspace.blocks.labels.interpretation',
      })
    );
  });

  return blocks;
};

export const getReportModulesByType = (reportType) => {
  if (reportType && REPORT_MODULES_BY_TYPE[reportType]) {
    return REPORT_MODULES_BY_TYPE[reportType];
  }
  return REPORT_MODULES_BY_TYPE.relazione_agronomica_primo_anno;
};

export const getInitialModuleBlocks = (reportType, moduleId, locale) => {
  const normalizedType = reportType || 'relazione_agronomica_primo_anno';
  let blocks = [];
  if (normalizedType === 'relazione_agronomica_annuale') {
    blocks = moduleId === 'm1'
      ? buildProductionTrendBlocks(moduleId, normalizedType, locale)
      : buildAnnualBlocks(moduleId, normalizedType, locale);
  } else {
    blocks = buildFirstYearBlocks(moduleId, normalizedType, locale);
  }
  const localizedBlocks = (blocks || []).map(block => ({ ...block, locale }));
  return cloneBlocks(localizedBlocks);
};
